import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Button, TextField, Grid, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel, Chip, Card, CardContent, CardMedia, IconButton,
  Tabs, Tab, Divider, Alert, CircularProgress, Tooltip, Autocomplete,
  Slider, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  Link as LinkIcon, ContentCopy, QrCode2, Delete, Add, Palette,
  ViewModule, ViewList, ViewCarousel, Visibility, Download, Share,
  ShoppingCart, LocalOffer, Image as ImageIcon, ColorLens, CloudUpload,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { HEADERS, HEADER_FORM_DATA } from '../../../API/UrlAndPaths';

// Theme presets
const THEMES = {
  modern: { bg: '#ffffff', text: '#333333', accent: '#2196F3', button: '#4CAF50' },
  classic: { bg: '#f5f5dc', text: '#2c1810', accent: '#8B4513', button: '#D2691E' },
  minimal: { bg: '#fafafa', text: '#212121', accent: '#757575', button: '#424242' },
  festive: { bg: '#fff8e1', text: '#e65100', accent: '#ff6f00', button: '#ff5722' },
  dark: { bg: '#1a1a2e', text: '#eaeaea', accent: '#e94560', button: '#0f3460' }
};

const CTALinkBuilder = ({ campaignId, campaignName, onClose, onCreated, onSendWithLink }) => {
  // Get cached products from Redux (loaded by BillingNew)
  const cachedProductsList = useSelector(state => state.newBillingSlice?.productsList);
  
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [formData, setFormData] = useState({
    title: campaignName || 'Special Offer',
    description: '',
    offerDetails: '',
    ctaButtonText: 'Order Now',
    ctaButtonColor: '#4CAF50',
    headerImage: '',
    logoUrl: '',
    backgroundColor: '#ffffff',
    textColor: '#333333',
    accentColor: '#FF5722',
    layout: 'grid',
    theme: 'modern',
    showPrices: true,
    showDescriptions: true,
    expiryDate: '',
    redirectUrl: '',
    contactMethod: 'WHATSAPP',
    contactValue: ''
  });

  const resolveAssetUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    if (url.startsWith('blob:') || url.startsWith('data:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;

    const baseUrl = (axios.defaults.baseURL || '').replace(/\/$/, '');
    if (!baseUrl) return url;
    if (url.startsWith('/')) return `${baseUrl}${url}`;
    return `${baseUrl}/${url}`;
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [cachedProductsList]);

  const fetchProducts = async () => {
    try {
      // Priority 1: Use Redux cached products (loaded by BillingNew)
      if (cachedProductsList && cachedProductsList.length > 0) {
        console.log('📦 Using Redux cached products:', cachedProductsList.length);
        if (cachedProductsList[0]) {
          console.log('📦 Sample cached product:', cachedProductsList[0]?.productName, 'imageUrl:', cachedProductsList[0]?.imageUrl);
        }
        setProducts(cachedProductsList);
        return;
      }
      
      // Priority 2: Check sessionStorage cache
      const sessionCached = sessionStorage.getItem('productsData');
      if (sessionCached) {
        try {
          const parsedProducts = JSON.parse(sessionCached);
          if (Array.isArray(parsedProducts) && parsedProducts.length > 0) {
            console.log('📦 Using sessionStorage cached products:', parsedProducts.length);
            setProducts(parsedProducts);
            return;
          }
        } catch (e) {
          console.warn('Failed to parse sessionStorage products');
        }
      }
      
      // Priority 3: Fetch from server
      const res = await axios.post('readymade/viewproductsforpos', {}, HEADERS);
      if (res.status === 200) {
        const productList = res.data || [];
        if (productList.length > 0) {
          console.log('📦 Products loaded from server:', productList.length);
          console.log('📦 Sample product:', productList[0]?.productName, 'imageUrl:', productList[0]?.imageUrl);
        }
        setProducts(productList);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      // Use the category list endpoint
      const res = await axios.post('category/view', {}, HEADERS);
      if (res.status === 200) {
        setCategories(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Handle image upload
  const handleImageUpload = async (event, type) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Please select an image file', 'error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire('Error', 'Image size should be less than 5MB', 'error');
      return;
    }

    const setUploading = type === 'header' ? setUploadingHeader : setUploadingLogo;
    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const res = await axios.post('/api/upload/image', formDataUpload, HEADER_FORM_DATA);

      if (res.data?.success && res.data?.url) {
        setFormData(prev => ({
          ...prev,
          [type === 'header' ? 'headerImage' : 'logoUrl']: resolveAssetUrl(res.data.url)
        }));
        Swal.fire({ icon: 'success', title: 'Image uploaded!', timer: 1500, showConfirmButton: false });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      // Fallback: Use local file URL for preview (won't persist but shows preview)
      const localUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        [type === 'header' ? 'headerImage' : 'logoUrl']: localUrl
      }));
      Swal.fire({
        icon: 'info',
        title: 'Preview Mode',
        text: 'Image shown as preview. For production, configure image upload endpoint.',
        timer: 3000
      });
    } finally {
      setUploading(false);
    }
  };

  // Remove uploaded image
  const handleRemoveImage = (type) => {
    setFormData(prev => ({
      ...prev,
      [type === 'header' ? 'headerImage' : 'logoUrl']: ''
    }));
  };

  const applyTheme = (themeName) => {
    const theme = THEMES[themeName];
    if (theme) {
      setFormData(prev => ({
        ...prev,
        theme: themeName,
        backgroundColor: theme.bg,
        textColor: theme.text,
        accentColor: theme.accent,
        ctaButtonColor: theme.button
      }));
    }
  };

  const handleCreateLink = async () => {
    if (selectedProducts.length === 0) {
      Swal.fire('Warning', 'Please select at least one product', 'warning');
      return;
    }

    setLoading(true);
    try {
      // Map products to the format expected by the backend
      const productData = selectedProducts.map(p => ({
        productId: p._id,
        name: p.name || p.productName,
        description: p.description || '',
        price: p.salesPrice || p.price || 0,
        image: getProductImage(p) || ''
      }));

      const res = await axios.post('/api/campaign-links/create', {
        campaignId,
        ...formData,
        contact: formData.redirectUrl
          ? null
          : {
              method: formData.contactMethod,
              value: formData.contactValue
            },
        products: selectedProducts.map(p => p._id),
        productDetails: productData // Send full product details
      }, HEADERS);

      if (res.data.success) {
        setGeneratedLink(res.data.data);
        Swal.fire('Success', 'CTA Link created successfully!', 'success');
        onCreated?.(res.data.data);
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to create link', 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    Swal.fire({ icon: 'success', title: 'Copied!', timer: 1000, showConfirmButton: false });
  };

  // Helper to get product image URL (handles both string and array)
  const getProductImage = (product) => {
    if (!product) return null;
    
    // Try different image field names used in the system
    const imgUrl = product.imageUrl || product.images || product.image || product.productImage;
    
    if (!imgUrl || (Array.isArray(imgUrl) && imgUrl.length === 0)) {
      return null; // No image available
    }
    
    let finalUrl = Array.isArray(imgUrl) ? imgUrl[0] : imgUrl;
    
    // Skip if empty string
    if (!finalUrl || finalUrl.trim() === '') {
      return null;
    }
    
    // If URL contains a different IP/domain, convert to use current axios baseURL
    // This handles cases where backend returns http://192.168.1.55:3100/Images/...
    // but frontend connects to localhost:3100
    if (finalUrl && typeof finalUrl === 'string') {
      // Extract path starting from /Images/
      const imagesMatch = finalUrl.match(/\/Images\/.*/);
      if (imagesMatch) {
        // Use axios baseURL, remove trailing slash if present
        const baseUrl = (axios.defaults.baseURL || '').replace(/\/$/, '');
        finalUrl = baseUrl + imagesMatch[0];
      }
    }
    
    return finalUrl;
  };

  const renderProductSelector = () => (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Select Products to Feature
      </Typography>
      
      <Autocomplete
        multiple
        options={products}
        getOptionLabel={(option) => option.name || option.productName || ''}
        isOptionEqualToValue={(option, value) => option?._id === value?._id}
        value={selectedProducts}
        onChange={(e, newValue) => setSelectedProducts(newValue)}
        renderInput={(params) => (
          <TextField {...params} placeholder="Search and select products..." />
        )}
        renderOption={(props, option) => {
          // Use _id as key to avoid duplicate key warnings (products can have same name with different variants)
          const { key, ...otherProps } = props;
          const imgUrl = getProductImage(option);
          return (
            <Box component="li" key={option._id || key} {...otherProps} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {imgUrl && (
                <img src={imgUrl} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
              )}
              <Box>
                <Typography variant="body2">{option.name || option.productName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {option.prodId} {option.salesPrice ? `• ₹${option.salesPrice}` : ''}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.filter(v => v != null).map((option, index) => {
            const imgUrl = getProductImage(option);
            return (
              <Chip
                {...getTagProps({ index })}
                key={option?._id || index}
                label={option?.name || option?.productName || 'Unknown'}
                avatar={imgUrl ? <img src={imgUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} /> : null}
              />
            );
          })
        }
        sx={{ mb: 2 }}
      />

      {/* Selected Products Preview */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {selectedProducts.map((product, index) => {
          const imgUrl = getProductImage(product);
          return (
            <Card key={product._id || index} sx={{ width: { xs: '45%', sm: '30%', md: '22%' } }}>
              {imgUrl && (
                <CardMedia
                  component="img"
                  height="100"
                  image={imgUrl}
                  alt={product.name || product.productName}
                  sx={{ objectFit: 'cover' }}
                />
              )}
              <CardContent sx={{ p: 1 }}>
                <Typography variant="body2" noWrap>{product.name || product.productName}</Typography>
                <Typography variant="caption" color="primary">
                  {product.salesPrice ? `₹${product.salesPrice}` : product.prodId}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );

  const renderDesignTab = () => (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Theme Presets
      </Typography>
      <ToggleButtonGroup
        value={formData.theme}
        exclusive
        onChange={(e, val) => val && applyTheme(val)}
        sx={{ mb: 3, flexWrap: 'wrap' }}
      >
        {Object.keys(THEMES).map((theme) => (
          <ToggleButton key={theme} value={theme} sx={{ textTransform: 'capitalize' }}>
            <Box
              sx={{
                width: 20, height: 20, borderRadius: '50%',
                bgcolor: THEMES[theme].button, mr: 1
              }}
            />
            {theme}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Layout Style
      </Typography>
      <ToggleButtonGroup
        value={formData.layout}
        exclusive
        onChange={(e, val) => val && setFormData(prev => ({ ...prev, layout: val }))}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="grid"><ViewModule sx={{ mr: 1 }} /> Grid</ToggleButton>
        <ToggleButton value="list"><ViewList sx={{ mr: 1 }} /> List</ToggleButton>
        <ToggleButton value="carousel"><ViewCarousel sx={{ mr: 1 }} /> Carousel</ToggleButton>
      </ToggleButtonGroup>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Background Color"
            type="color"
            value={formData.backgroundColor}
            onChange={(e) => setFormData(prev => ({ ...prev, backgroundColor: e.target.value }))}
            InputProps={{ sx: { height: 56 } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Text Color"
            type="color"
            value={formData.textColor}
            onChange={(e) => setFormData(prev => ({ ...prev, textColor: e.target.value }))}
            InputProps={{ sx: { height: 56 } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Accent Color"
            type="color"
            value={formData.accentColor}
            onChange={(e) => setFormData(prev => ({ ...prev, accentColor: e.target.value }))}
            InputProps={{ sx: { height: 56 } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Button Color"
            type="color"
            value={formData.ctaButtonColor}
            onChange={(e) => setFormData(prev => ({ ...prev, ctaButtonColor: e.target.value }))}
            InputProps={{ sx: { height: 56 } }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.showPrices}
                onChange={(e) => setFormData(prev => ({ ...prev, showPrices: e.target.checked }))}
              />
            }
            label="Show Prices"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.showDescriptions}
                onChange={(e) => setFormData(prev => ({ ...prev, showDescriptions: e.target.checked }))}
              />
            }
            label="Show Descriptions"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderContentTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Page Title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="e.g., Weekend Special Offers"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Describe your offer..."
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Offer Details"
          value={formData.offerDetails}
          onChange={(e) => setFormData(prev => ({ ...prev, offerDetails: e.target.value }))}
          placeholder="e.g., Get 20% OFF on all items!"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="CTA Button Text"
          value={formData.ctaButtonText}
          onChange={(e) => setFormData(prev => ({ ...prev, ctaButtonText: e.target.value }))}
          placeholder="Order Now"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Redirect URL (after CTA click)"
          value={formData.redirectUrl}
          onChange={(e) => setFormData(prev => ({ ...prev, redirectUrl: e.target.value }))}
          placeholder="https://your-ordering-page.com"
        />
      </Grid>
      {!formData.redirectUrl && (
        <>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Contact Method (if no redirect URL)</InputLabel>
              <Select
                value={formData.contactMethod}
                label="Contact Method (if no redirect URL)"
                onChange={(e) => setFormData(prev => ({ ...prev, contactMethod: e.target.value }))}
              >
                <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                <MenuItem value="MOBILE">Mobile Call</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
                <MenuItem value="KOOPPAI">Kooppai</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact Value"
              value={formData.contactValue}
              onChange={(e) => setFormData(prev => ({ ...prev, contactValue: e.target.value }))}
              placeholder={
                formData.contactMethod === 'KOOPPAI'
                  ? 'Kooppai ID / Username'
                  : 'Phone Number'
              }
            />
          </Grid>
        </>
      )}
      {/* Header Image Upload */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>Header Image</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {formData.headerImage ? (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Box
                component="img"
                src={resolveAssetUrl(formData.headerImage)}
                alt="Header Preview"
                sx={{ 
                  width: 200, 
                  height: 100, 
                  objectFit: 'cover', 
                  borderRadius: 1,
                  border: '1px solid #ddd'
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveImage('header')}
                sx={{ 
                  position: 'absolute', 
                  top: -8, 
                  right: -8, 
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'error.dark' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Button
              variant="outlined"
              component="label"
              startIcon={uploadingHeader ? <CircularProgress size={20} /> : <CloudUpload />}
              disabled={uploadingHeader}
              sx={{ height: 100, width: 200 }}
            >
              {uploadingHeader ? 'Uploading...' : 'Upload Header'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'header')}
              />
            </Button>
          )}
          <Typography variant="caption" color="textSecondary">
            Recommended: 1200x400px, Max 5MB
          </Typography>
        </Box>
      </Grid>

      {/* Logo Upload */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>Logo</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {formData.logoUrl ? (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Box
                component="img"
                src={resolveAssetUrl(formData.logoUrl)}
                alt="Logo Preview"
                sx={{ 
                  width: 100, 
                  height: 100, 
                  objectFit: 'contain', 
                  borderRadius: 1,
                  border: '1px solid #ddd',
                  bgcolor: '#f5f5f5',
                  p: 1
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveImage('logo')}
                sx={{ 
                  position: 'absolute', 
                  top: -8, 
                  right: -8, 
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'error.dark' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Button
              variant="outlined"
              component="label"
              startIcon={uploadingLogo ? <CircularProgress size={20} /> : <CloudUpload />}
              disabled={uploadingLogo}
              sx={{ height: 100, width: 100 }}
            >
              {uploadingLogo ? '...' : 'Logo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'logo')}
              />
            </Button>
          )}
          <Typography variant="caption" color="textSecondary">
            Recommended: 200x200px, Max 5MB
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="datetime-local"
          label="Expiry Date (Optional)"
          value={formData.expiryDate}
          onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
    </Grid>
  );

  const renderPreview = () => (
    <Paper
      sx={{
        p: 2,
        bgcolor: formData.backgroundColor,
        color: formData.textColor,
        borderRadius: 2,
        minHeight: 400
      }}
    >
      {formData.headerImage && (
        <Box
          component="img"
          src={resolveAssetUrl(formData.headerImage)}
          alt="Header"
          sx={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 1, mb: 2 }}
        />
      )}
      
      <Typography variant="h5" fontWeight={700} sx={{ color: formData.accentColor }} gutterBottom>
        {formData.title || 'Your Offer Title'}
      </Typography>
      
      {formData.description && (
        <Typography variant="body2" sx={{ mb: 2 }}>{formData.description}</Typography>
      )}
      
      {formData.offerDetails && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2, 
            bgcolor: `${formData.accentColor}20`,
            color: formData.textColor,
            '& .MuiAlert-icon': { color: formData.accentColor }
          }}
        >
          <LocalOffer sx={{ mr: 1, fontSize: 16 }} />
          {formData.offerDetails}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {selectedProducts.slice(0, 4).map((product, index) => {
          const imgUrl = getProductImage(product);
          return (
            <Card 
              key={product._id || index} 
              sx={{ 
                display: formData.layout === 'list' ? 'flex' : 'block',
                width: formData.layout === 'list' ? '100%' : '48%'
              }}
            >
              {imgUrl && (
                <CardMedia
                  component="img"
                  sx={{ 
                    height: formData.layout === 'list' ? 80 : 100,
                    width: formData.layout === 'list' ? 80 : '100%',
                    objectFit: 'cover'
                  }}
                  image={imgUrl}
                  alt={product.name || product.productName}
                />
              )}
              <CardContent sx={{ p: 1, flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{product.name || product.productName}</Typography>
                {formData.showPrices && product.salesPrice && (
                  <Typography variant="caption" sx={{ color: formData.accentColor }}>
                    ₹{product.salesPrice}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        sx={{
          mt: 2,
          bgcolor: formData.ctaButtonColor,
          '&:hover': { bgcolor: formData.ctaButtonColor, filter: 'brightness(0.9)' }
        }}
      >
        {formData.ctaButtonText}
      </Button>
    </Paper>
  );

  const renderGeneratedLink = () => (
    <Paper sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h6" color="success.main" gutterBottom>
        🎉 Your CTA Link is Ready!
      </Typography>
      
      <Box sx={{ 
        p: 2, bgcolor: 'grey.100', borderRadius: 2, mb: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1
      }}>
        <LinkIcon color="primary" />
        <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
          {generatedLink.fullUrl}
        </Typography>
        <IconButton onClick={() => copyToClipboard(generatedLink.fullUrl)} color="primary">
          <ContentCopy />
        </IconButton>
      </Box>

      <Grid container spacing={2} justifyContent="center">
        <Grid item>
          <Button
            variant="outlined"
            startIcon={<QrCode2 />}
            onClick={() => window.open(generatedLink.qrCodeUrl, '_blank')}
          >
            Download QR Code
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={() => window.open(generatedLink.fullUrl, '_blank')}
          >
            Preview Link
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<Share />}
            onClick={() => {
              if (navigator.share) {
                navigator.share({ title: formData.title, url: generatedLink.fullUrl });
              } else {
                copyToClipboard(generatedLink.fullUrl);
              }
            }}
          >
            Share
          </Button>
        </Grid>
        {onSendWithLink && (
          <Grid item>
            <Button
              variant="contained"
              color="success"
              onClick={() => onSendWithLink(generatedLink)}
            >
              📧 Send Campaign with Link
            </Button>
          </Grid>
        )}
      </Grid>

      {generatedLink.qrCodeUrl && (
        <Box sx={{ mt: 3 }}>
          <img src={generatedLink.qrCodeUrl} alt="QR Code" style={{ width: 150, height: 150 }} />
        </Box>
      )}
    </Paper>
  );

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LinkIcon color="primary" />
          Create CTA Link for Campaign
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {generatedLink ? (
          renderGeneratedLink()
        ) : (
          <Grid container spacing={3}>
            {/* Left Panel - Configuration */}
            <Grid item xs={12} md={7}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                <Tab icon={<ShoppingCart />} label="Products" />
                <Tab icon={<Palette />} label="Design" />
                <Tab icon={<LocalOffer />} label="Content" />
              </Tabs>

              {activeTab === 0 && renderProductSelector()}
              {activeTab === 1 && renderDesignTab()}
              {activeTab === 2 && renderContentTab()}
            </Grid>

            {/* Right Panel - Preview */}
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Live Preview
              </Typography>
              {renderPreview()}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {generatedLink ? 'Close' : 'Cancel'}
        </Button>
        {!generatedLink && (
          <Button
            variant="contained"
            onClick={handleCreateLink}
            disabled={loading || selectedProducts.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <Add />}
          >
            Generate CTA Link
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CTALinkBuilder;
